import React, { useState, useEffect } from 'react';
import { leaveAPI } from '../../services/api';
import { toast } from 'react-toastify';
import './LeaveRequestView.css';

const LeaveRequestView = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    leave_type: 'CASUAL',
    start_date: '',
    end_date: '',
    reason: ''
  });

  useEffect(() => {
    fetchLeaveRequests();
  }, []);

  const fetchLeaveRequests = async () => {
    try {
      setLoading(true);
      const response = await leaveAPI.getMyLeaveRequests();
      setLeaveRequests(response.data.leaveRequests || []);
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      toast.error('End date must be after start date');
      return;
    }

    try {
      setLoading(true);
      await leaveAPI.submitLeaveRequest(formData);
      toast.success('Leave request submitted successfully!');
      setShowForm(false);
      setFormData({
        leave_type: 'CASUAL',
        start_date: '',
        end_date: '',
        reason: ''
      });
      fetchLeaveRequests();
    } catch (error) {
      console.error('Failed to submit leave request:', error);
      toast.error(error.response?.data?.error || 'Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'orange';
      case 'MANAGER_APPROVED': return 'blue';
      case 'ADMIN_APPROVED': return 'green';
      case 'REJECTED': return 'red';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'PENDING': return 'Pending';
      case 'MANAGER_APPROVED': return 'Manager Approved';
      case 'ADMIN_APPROVED': return 'Approved';
      case 'REJECTED': return 'Rejected';
      default: return status;
    }
  };

  return (
    <div className="leave-request-view">
      <div className="leave-header">
        <h2>Leave Requests</h2>
        <button className="btn-new-leave" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ New Leave Request'}
        </button>
      </div>

      {showForm && (
        <div className="leave-form-container">
          <h3>Submit Leave Request</h3>
          <form onSubmit={handleSubmit} className="leave-form">
            <div className="form-row">
              <div className="form-group">
                <label>Leave Type</label>
                <select
                  value={formData.leave_type}
                  onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                  required
                >
                  <option value="CASUAL">Casual Leave</option>
                  <option value="SICK">Sick Leave</option>
                  <option value="EARNED">Earned Leave</option>
                  <option value="LOP">Loss of Pay</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Reason</label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows="4"
                placeholder="Enter reason for leave..."
                required
              />
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        </div>
      )}

      <div className="leave-requests-list">
        <h3>My Leave Requests</h3>
        {loading && <p>Loading...</p>}
        {!loading && leaveRequests.length === 0 && (
          <p className="no-data">No leave requests found</p>
        )}
        {!loading && leaveRequests.length > 0 && (
          <div className="leave-table-container">
            <table className="leave-table">
              <thead>
                <tr>
                  <th>Leave Type</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Days</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Applied On</th>
                </tr>
              </thead>
              <tbody>
                {leaveRequests.map((request) => {
                  const days = Math.ceil((new Date(request.end_date) - new Date(request.start_date)) / (1000 * 60 * 60 * 24)) + 1;
                  return (
                    <tr key={request.id}>
                      <td>{request.leave_type}</td>
                      <td>{new Date(request.start_date).toLocaleDateString()}</td>
                      <td>{new Date(request.end_date).toLocaleDateString()}</td>
                      <td>{days}</td>
                      <td>{request.reason}</td>
                      <td>
                        <span className="status-badge" style={{ backgroundColor: getStatusColor(request.status) }}>
                          {getStatusText(request.status)}
                        </span>
                      </td>
                      <td>{new Date(request.applied_at).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveRequestView;
